import type { AuthMe } from "@iec-id/shared/contracts/auth";
import { notify } from "@iec-id/ui";
import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { getQueryKey } from "@trpc/react-query";
import { i18n } from "./i18n.js";
import { isUnauthorized, redirectToSignIn } from "./session.js";
import { api } from "./trpc.js";

/**
 * Сервер режет мутации, пока идёт отсрочка удаления, и кладёт машинный код в
 * текст ошибки (`shared/trpc/procedures.ts`). Без разбора человек получал бы
 * «что-то пошло не так» на КАЖДОЕ действие и не понимал, что аккаунт просто
 * помечен на удаление, а решение обратимо.
 */
const PENDING_DELETION = "account_pending_deletion";

function isPendingDeletion(error: unknown): boolean {
	const message = (error as { message?: string } | null)?.message;
	return typeof message === "string" && message.startsWith(PENDING_DELETION);
}

/**
 * Общие настройки серверного стейта. `staleTime` в минуту убирает
 * перезапросы при переключении разделов витрины; ошибки 4xx не ретраятся —
 * повтор запрещённого запроса бессмыслен.
 *
 * Перехват 401 стоит на обоих кэшах, а не в ссылке tRPC: `httpBatchLink`
 * раскладывает один HTTP-ответ по нескольким запросам, и ошибка доезжает до
 * каждого из них уже разобранной — ловить её на уровне кэша и проще, и полнее
 * (мутации туда тоже попадают).
 */
export function createQueryClient(initialMe?: AuthMe | undefined): QueryClient {
	const onError = (error: unknown): void => {
		if (isUnauthorized(error)) redirectToSignIn();
	};

	const onMutationError = (error: unknown): void => {
		onError(error);
		// `i18n.t`, а не `useTranslation`: перехватчик живёт вне дерева React,
		// но внутри того же экземпляра i18n — язык у него общий с интерфейсом.
		if (isPendingDeletion(error)) {
			notify.error(i18n.t("common:errors.pendingDeletionTitle"), {
				description: i18n.t("common:errors.pendingDeletionDescription"),
			});
		}
	};

	const client = new QueryClient({
		queryCache: new QueryCache({ onError }),
		mutationCache: new MutationCache({ onError: onMutationError }),
		defaultOptions: {
			queries: {
				staleTime: 60_000,
				gcTime: 5 * 60_000,
				refetchOnWindowFocus: false,
				retry: (failureCount, error) => {
					const status = (error as { data?: { httpStatus?: number } }).data?.httpStatus;
					if (status && status >= 400 && status < 500) return false;
					return failureCount < 2;
				},
			},
			mutations: { retry: false },
		},
	});

	// Профиль, полученный ДО первого кадра (`session-boot.ts`), кладётся в кэш
	// как обычный ответ `auth.me`. Без этого витрина спрашивала бы то же самое
	// второй раз и показывала бы спиннер `SessionGate` поверх уже известного
	// ответа — то есть загрузку того, что уже загружено.
	if (initialMe) {
		client.setQueryData(getQueryKey(api.auth.me, undefined, "query"), initialMe);
	}

	return client;
}
